import React, { useState, useEffect } from 'react'
import { globalState } from '../../store'

// Components
import CardLoading from '../../components/component-items/loading-popover'
import GlobalStyles from '../../components/component-items/styles'
import { useFetch } from '../../components/component-items/helpers'
import { THSort } from '../../components/component-items/datatable'
import { SearchBox } from '../../components/component-items/search'
import { BatchListCellCustom, BatchListRowStyled, BatchListStyled, DataTable, StatusBox } from './styles'
import ReactTimeAgo from 'react-time-ago'

type CarrierListCellProps = {
  name?: string
  center?: any
  children?: any
  input?: any
  width?: any
  grading?: string
}

const CarrierListCell = ({ name, input, children, center, grading }: CarrierListCellProps) => (
  <BatchListCellCustom className={name === 'Total' ? 'total' : ''}>
    <div style={{ textAlign: center ? 'center' : 'left' }}>
      <span className="input">{input}</span>
      {grading && (
        <>
          <br />
          <small>({grading})</small>
        </>
      )}
      {children}
    </div>
  </BatchListCellCustom>
)

type CarrierListRowProps = {
  key?: any
  id?: any
  name: string
  overdue: string
  delayedPickup: string
  awaitingArrival: string
  missing: string
  carrierException: string
  rts: string
  grading: string
  setSearch: any
  hideOverdue?: boolean
}

const CarrierListRow = ({
  id,
  name,
  overdue,
  delayedPickup,
  awaitingArrival,
  missing,
  carrierException,
  rts,
  grading,
  setSearch,
  hideOverdue,
}: CarrierListRowProps) => {
  return (
    <BatchListRowStyled className={name === 'Total' ? 'total' : ''}>
      <div onClick={name !== 'Total' ? () => setSearch(name) : undefined}>
        <CarrierListCell name={name} grading={grading} input={name} />
      </div>
      {!hideOverdue && (
        <CarrierListCell name={name} input={<StatusBox className="silver">{overdue}</StatusBox>} center />
      )}
      <CarrierListCell name={name} input={<StatusBox className="silver">{delayedPickup}</StatusBox>} center />
      <CarrierListCell name={name} input={<StatusBox className="silver">{awaitingArrival}</StatusBox>} center />
      <CarrierListCell name={name} input={<StatusBox className="silver">{missing}</StatusBox>} center />
      <CarrierListCell name={name} input={<StatusBox className="silver">{carrierException}</StatusBox>} center />
      <CarrierListCell name={name} input={<StatusBox className="silver">{rts}</StatusBox>} center />
    </BatchListRowStyled>
  )
}

export const CarrierDelays = ({ hideOverdue }: { hideOverdue?: boolean }) => {
  const {
    state: { warehouseActive, userIsWarehouse, companyId },
  } = globalState()
  const [sort, setSort] = useState({ total: 'desc' })
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const defaultQueryParamsString = {
    order_by: Object.keys(sort),
    sort: Object.values(sort),
    q: search,
  }
  const base_url = userIsWarehouse
    ? `/api/warehouse/${warehouseActive.id ?? 'all'}/dashboard/carrier_outbound_exceptions/?`
    : `/api/company/${companyId}/dashboard/carrier_outbound_exceptions/?`
  const download_url = base_url + '&download=true'
  const list_url =
    base_url +
    `&${Object.entries(defaultQueryParamsString)
      .map(([key, value]) => `${key}=${value}`)
      .join('&')}`
  const { response, loaded, error }: any = useFetch(list_url, {})
  const carriers: any = loaded && response ? response.results : []
  const lastUpdated = loaded && response ? response.lastUpdated : null

  useEffect(() => {
    if (loaded && !error) {
      setLoading(false)
    } else {
      setLoading(true)
    }
  }, [loaded])

  const download = (e: any) => {
    e.preventDefault()
    window.location.href = download_url
  }

  return (
    <BatchListStyled style={{ position: 'relative' }}>
      <div className="datatable__header">
        <div className="datatable__title">
          <h3>Exceptions By Carrier</h3>
          <small className="ps-3">
            Last Updated: {lastUpdated ? <ReactTimeAgo date={Date.parse(lastUpdated)} locale="en-US" /> : 'NA'}
          </small>
        </div>
        <GlobalStyles.CardToolbar>
          <GlobalStyles.Button className="secondary" onClick={download}>
            Download
          </GlobalStyles.Button>
          <SearchBox {...{ search, setSearch, loading, setLoading, page, setPage }} setFocus />
        </GlobalStyles.CardToolbar>
      </div>
      <div className="card-body" style={{ position: 'relative' }}>
        <DataTable>
          <thead>
            <tr>
              <th>
                <span className="text">Carrier</span>
              </th>
              {!hideOverdue && <THSort center title={'Overdue'} {...{ sort, setSort }} />}
              <THSort center title={'Delayed Pickup'} {...{ sort, setSort }} />
              <THSort center title={'Awaiting Arrival'} {...{ sort, setSort }} />
              <THSort center title={'Missing'} {...{ sort, setSort }} />
              <THSort center title={'Carrier Exception'} {...{ sort, setSort }} />
              <THSort center title={'RTS'} {...{ sort, setSort }} />
            </tr>
          </thead>
          <tbody>
            {carriers?.map((c: any, id: Number) => (
              <CarrierListRow {...c} setSearch={setSearch} key={id} hideOverdue={hideOverdue} />
            ))}
          </tbody>
        </DataTable>
        {!loaded ? <CardLoading error={error} /> : null}
      </div>
    </BatchListStyled>
  )
}

export default CarrierDelays
